import { useMemo, useState } from 'react';
import { Check, Loader2, ShieldCheck } from 'lucide-react';
import Unterschrift from '@/components/Unterschrift';

/* Unterschrift-Station (Bauauftrag 03.10.2026): die EINE Ansicht fuer das Theken-iPad (/unterschrift)
   und das Handy des Kunden (/u/<token>). Gezeigt wird genau die `anzeige` des Servers — derselbe
   Wortlaut wie im Vertrags-PDF, ueber den auch der dokument_hash laeuft. Hier wird nichts gerechnet
   und nichts umformuliert.

   Gestaltung nach dem P1-Design (Grundflaeche #0d0d0d, Akzent #ef7c23, Anton fuer Titel und Betraege,
   Inter fuer Text). Der Vertragstext selbst steht auf Papierweiss: lange Rechtstexte liest man hell
   besser, und die Unterschrift erscheint so, wie sie im PDF steht. */

export const UV_STIL = `
.uv{--uv-bg:#0d0d0d;--uv-flaeche:#141414;--uv-kante:rgba(255,255,255,.12);--uv-orange:#ef7c23;--uv-orange-hell:#f68f3f;
  --uv-text:rgba(255,255,255,.72);--uv-still:rgba(255,255,255,.46);--uv-papier:#faf9f7;--uv-tinte:#17181c;--uv-tinte-still:#5b5f69;
  min-height:100vh;min-height:100dvh;background:var(--uv-bg);color:#fff;font-family:Inter,-apple-system,system-ui,sans-serif;
  font-size:16px;line-height:1.5;-webkit-font-smoothing:antialiased;-webkit-text-size-adjust:100%}
.uv *{box-sizing:border-box}
.uv-kopf{display:flex;align-items:center;justify-content:space-between;gap:16px;padding:14px 20px;border-bottom:1px solid var(--uv-kante)}
.uv-kopf img{height:26px;width:auto;display:block}
.uv-kopf span{font-size:12px;letter-spacing:.14em;text-transform:uppercase;color:var(--uv-still)}
.uv-titel{font-family:Anton,Impact,sans-serif;font-weight:400;letter-spacing:1.4px;text-transform:uppercase;line-height:1.08;margin:0;text-wrap:balance}
.uv-warten{min-height:calc(100dvh - 55px);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:22px;padding:32px;text-align:center}
.uv-warten img{height:74px;width:auto;opacity:.92}
.uv-warten .uv-titel{font-size:clamp(30px,5vw,54px)}
.uv-warten p{margin:0;color:var(--uv-text);max-width:34ch}
.uv-puls{width:9px;height:9px;border-radius:99px;background:var(--uv-orange);animation:uv-puls 1.8s ease-in-out infinite}
@keyframes uv-puls{0%,100%{opacity:.25;transform:scale(.8)}50%{opacity:1;transform:scale(1.15)}}
@media (prefers-reduced-motion:reduce){.uv-puls{animation:none;opacity:.8}}
.uv-raster{display:grid;gap:18px;padding:18px 20px 28px;max-width:1280px;margin:0 auto}
@media (min-width:900px){.uv-raster.uv-zwei{grid-template-columns:minmax(0,1.15fr) minmax(360px,.85fr);align-items:start}
  .uv-raster.uv-zwei .uv-papier{max-height:calc(100dvh - 200px)}
  .uv-raster.uv-zwei .uv-handlung{position:sticky;top:16px}}
.uv-begruessung{padding:20px 20px 0;max-width:1280px;margin:0 auto}
.uv-begruessung .uv-titel{font-size:clamp(26px,3.6vw,40px)}
.uv-begruessung p{margin:6px 0 0;color:var(--uv-text)}
.uv-karte{background:var(--uv-flaeche);border:1px solid var(--uv-kante);border-radius:16px;padding:16px 18px}
.uv-zus{display:grid;grid-template-columns:max-content minmax(0,1fr);gap:7px 16px;margin:0;font-size:15px}
.uv-zus dt{color:var(--uv-still)}
.uv-zus dd{margin:0;color:#fff;font-weight:600;overflow-wrap:break-word;min-width:0}
.uv-zus dd.uv-betrag{font-family:Anton,Impact,sans-serif;font-weight:400;letter-spacing:.6px;font-size:19px;color:var(--uv-orange-hell)}
@media (max-width:560px){.uv-zus{grid-template-columns:minmax(0,1fr);gap:1px 0}
  .uv-zus dt{margin-top:9px;font-size:13px}.uv-zus>div:first-child dt{margin-top:0}
  .uv-begruessung,.uv-raster{padding-left:14px;padding-right:14px}.uv-papier{padding:18px 16px}.uv-kopf{padding:12px 14px}}
.uv-papier{background:var(--uv-papier);color:var(--uv-tinte);border-radius:16px;padding:22px 24px;overflow-y:auto;-webkit-overflow-scrolling:touch;font-size:14.5px;line-height:1.55}
.uv-einspaltig .uv-papier{max-height:58dvh}
@media (max-width:899px){.uv-papier{max-height:58dvh}}   /* iPad hochkant: Text scrollt im Feld, Unterschrift bleibt erreichbar */
.uv-papier h3{font-size:16px;font-weight:700;margin:22px 0 8px;padding-top:16px;border-top:1px solid #e3e0da;text-wrap:balance}
.uv-papier h3:first-child{margin-top:0;padding-top:0;border-top:0}
.uv-papier h4{font-size:14px;font-weight:700;margin:14px 0 4px}
.uv-papier p{margin:0 0 9px;white-space:pre-line;max-width:72ch}
.uv-papier .uv-punkt{margin:0 0 6px;padding-left:16px;position:relative;max-width:72ch}
.uv-papier .uv-punkt::before{content:'';position:absolute;left:2px;top:.62em;width:5px;height:5px;border-radius:99px;background:var(--uv-orange)}
.uv-papier .uv-unter{margin:0 0 5px;padding-left:16px;color:var(--uv-tinte-still);max-width:72ch}
.uv-lesehinweis{font-size:12.5px;color:var(--uv-still);margin:8px 2px 0}
.uv-haken{display:flex;gap:12px;align-items:flex-start;padding:11px 12px;border:1px solid var(--uv-kante);border-radius:12px;cursor:pointer;min-height:48px;transition:border-color .15s,background .15s}
.uv-haken + .uv-haken{margin-top:8px}
.uv-haken input{position:absolute;opacity:0;width:1px;height:1px}
.uv-haken .uv-box{flex:0 0 24px;width:24px;height:24px;border-radius:7px;border:2px solid rgba(255,255,255,.38);display:flex;align-items:center;justify-content:center;margin-top:1px;transition:background .15s,border-color .15s}
.uv-haken .uv-box svg{width:15px;height:15px;opacity:0;color:#0d0d0d}
.uv-haken.uv-an{border-color:rgba(239,124,35,.6);background:rgba(239,124,35,.08)}
.uv-haken.uv-an .uv-box{background:var(--uv-orange);border-color:var(--uv-orange)}
.uv-haken.uv-an .uv-box svg{opacity:1}
.uv-haken:focus-within{outline:2px solid var(--uv-orange-hell);outline-offset:2px}
.uv-haken span.uv-htext{font-size:14.5px;color:var(--uv-text)}
.uv-haken.uv-an span.uv-htext{color:#fff}
.uv-abschnitt{font-size:12px;letter-spacing:.14em;text-transform:uppercase;color:var(--uv-still);margin:18px 0 9px}
.uv-abschnitt:first-child{margin-top:0}
.uv-feld{margin-top:10px}
.uv-feld .text-muted-foreground{color:var(--uv-still) !important}
.uv-feld label{color:var(--uv-text) !important}
.uv-feld button:hover{color:#fff !important}
.uv-name{font-size:13px;color:var(--uv-still);margin:4px 2px 0}
.uv-knopf{width:100%;margin-top:16px;min-height:58px;border:0;border-radius:14px;background:var(--uv-orange);color:#0d0d0d;font-family:Anton,Impact,sans-serif;
  font-size:21px;letter-spacing:1.3px;text-transform:uppercase;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:10px;transition:background .15s,opacity .15s}
.uv-knopf:hover:not(:disabled){background:var(--uv-orange-hell)}
.uv-knopf:disabled{opacity:.34;cursor:not-allowed}
.uv-knopf:focus-visible{outline:3px solid #fff;outline-offset:3px}
.uv-fehlt{font-size:13px;color:var(--uv-still);margin:9px 2px 0}
.uv-fehler{margin-top:12px;padding:11px 13px;border-radius:12px;background:rgba(220,38,38,.14);border:1px solid rgba(248,113,113,.5);color:#fecaca;font-size:14.5px}
.uv-hinweis{font-size:13px;color:var(--uv-still);margin:12px 2px 0}
.uv-erfolg{width:74px;height:74px;border-radius:99px;background:var(--uv-orange);color:#0d0d0d;display:flex;align-items:center;justify-content:center}
.uv-erfolg svg{width:40px;height:40px}
.uv-sekundaer{min-height:48px;padding:0 22px;border-radius:12px;border:1px solid rgba(255,255,255,.3);background:transparent;color:#fff;font:inherit;font-weight:600;cursor:pointer}
.uv-sekundaer:focus-visible{outline:2px solid var(--uv-orange-hell);outline-offset:2px}
.uv-eingabe{width:100%;min-height:48px;padding:0 14px;border-radius:12px;border:1px solid var(--uv-kante);background:rgba(255,255,255,.04);color:#fff;font:inherit;font-size:16px;box-sizing:border-box}
.uv-eingabe:focus{outline:2px solid var(--uv-orange-hell);outline-offset:1px}
.uv-eingabe + .uv-eingabe{margin-top:8px}
`;

/* Kontoänderung (Pia-Finanzen F2, 02.11.2026): das Mitglied tippt Kontoinhaber/in und IBAN selbst. Der Server prüft die Prüfziffer,
   speichert verschlüsselt und schickt die neue Anzeige (IBAN nur maskiert) — erst die wird bestätigt und unterschrieben. */
function BankEingabe({ bankSenden, vorschlag }) {
  const [inhaber, setInhaber] = useState(vorschlag || '');
  const [iban, setIban] = useState('');
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState('');
  const weiter = async () => {
    if (sendet) return;
    setSendet(true); setFehler('');
    try { await bankSenden({ kontoinhaber: inhaber.trim(), iban: iban.replace(/\s+/g, '').toUpperCase() }); }
    catch (e) { setFehler(e?.meldung || 'Die Bankverbindung konnte nicht übernommen werden.'); }
    setSendet(false);
  };
  return (
    <div data-testid="uv-bank">
      <p className="uv-abschnitt">Neue Bankverbindung</p>
      <input className="uv-eingabe" value={inhaber} onChange={(e) => setInhaber(e.target.value)} placeholder="Kontoinhaber/in" autoComplete="name" data-testid="uv-bank-inhaber" />
      <input className="uv-eingabe" value={iban} onChange={(e) => setIban(e.target.value)} placeholder="IBAN" autoComplete="off" inputMode="text"
             autoCapitalize="characters" spellCheck={false} data-testid="uv-bank-iban" />
      <button type="button" className="uv-knopf" style={{ marginTop: 12 }} disabled={sendet || inhaber.trim().length < 2 || iban.replace(/\s+/g, '').length < 15}
              onClick={weiter} data-testid="uv-bank-weiter">
        {sendet ? <><Loader2 className="w-5 h-5 animate-spin" /> Wird geprüft …</> : 'Weiter zum Mandat'}
      </button>
      {fehler && <div className="uv-fehler" role="alert" data-testid="uv-bank-fehler">{fehler}</div>}
    </div>
  );
}

function Block({ b }) {
  if (b.art === 'kopf') return <h4>{b.text}</h4>;
  if (b.art === 'punkt') return <div className="uv-punkt">{b.text}</div>;
  if (b.art === 'unterpunkt') return <div className="uv-unter">{b.text}</div>;
  return <p>{b.text}</p>;
}

/** vorgang: { id, anzeige, dokument_hash }; absenden(eingabe) -> Promise: wirft bei Ablehnung mit .meldung,
    gibt false zurueck, wenn der Vorgang nicht mehr gilt (die Seite schaltet dann selbst um). */
export default function VertragAnzeige({ vorgang, absenden, einspaltig = false, onFertig, bankSenden }) {
  const { anzeige } = vorgang;
  const [haken, setHaken] = useState({});
  const [zeiten, setZeiten] = useState({});
  const [unterschriften, setUnterschriften] = useState({});
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState('');

  const fehlt = useMemo(() => {
    const offen = anzeige.haken.filter((h) => h.pflicht && !haken[h.key]).length;
    const ohne = anzeige.unterschriften.filter((u) => !unterschriften[u.key]).length;
    const teile = [];
    if (offen) teile.push(offen === 1 ? 'eine Bestätigung' : `${offen} Bestätigungen`);
    if (ohne) teile.push(ohne === 1 ? 'eine Unterschrift' : `${ohne} Unterschriften`);
    return teile.join(' und ');
  }, [anzeige, haken, unterschriften]);

  const setze = (key, an) => {
    setHaken((h) => ({ ...h, [key]: an }));
    setZeiten((z) => ({ ...z, [key]: an ? new Date().toISOString() : undefined }));
  };

  const senden = async () => {
    if (fehlt || sendet) return;
    setSendet(true); setFehler('');
    try {
      const ok = await absenden({
        dokument_hash: vorgang.dokument_hash,
        haken: Object.fromEntries(anzeige.haken.map((h) => [h.key, !!haken[h.key]])),
        haken_zeiten: Object.fromEntries(Object.entries(zeiten).filter(([, v]) => v)),
        unterschriften,
        geraet: (navigator.userAgent || '').slice(0, 300),
      });
      if (ok === false) return;      // Vorgang ungueltig: die Seite hat selbst umgeschaltet
      onFertig?.();
    } catch (e) {
      setFehler(e?.meldung || 'Die Unterschrift konnte nicht übertragen werden. Bitte noch einmal versuchen.');
      setSendet(false);
    }
  };

  const betragLabels = ['Beitrag', 'Mit Gym & Sauna', 'Einmalig', 'Vorauszahlung'];
  return (
    <div className={einspaltig ? 'uv-einspaltig' : ''} data-testid="uv-vertrag">
      <div className="uv-begruessung">
        <h1 className="uv-titel">{anzeige.titel}</h1>
        <p>{anzeige.anbieter} · {anzeige.bank_eingabe && !anzeige.bank_erfasst ? 'Bitte die neue Bankverbindung eingeben.' : anzeige.haken.length > 0 ? 'Bitte lesen, bestätigen und unterschreiben.' : 'Bitte lesen und unterschreiben.'}</p>
      </div>
      <div className={`uv-raster ${einspaltig ? '' : 'uv-zwei'}`}>
        <div>
          <div className="uv-karte" data-testid="uv-zusammenfassung">
            <dl className="uv-zus">
              {anzeige.zusammenfassung.map((z) => (
                <div key={z.label} style={{ display: 'contents' }}>
                  <dt>{z.label}</dt>
                  <dd className={betragLabels.includes(z.label) ? 'uv-betrag' : ''}>{z.wert}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="uv-papier" style={{ marginTop: 14 }} tabIndex={0} data-testid="uv-text">
            {anzeige.abschnitte.map((a) => (
              <section key={a.titel}>
                <h3>{a.titel}</h3>
                {a.bloecke.map((b, i) => <Block key={i} b={b} />)}
              </section>
            ))}
          </div>
          <p className="uv-lesehinweis">Der vollständige Text lässt sich im hellen Feld scrollen.</p>
        </div>

        <div className="uv-karte uv-handlung">
          {anzeige.bank_eingabe && !anzeige.bank_erfasst ? (
            bankSenden ? <BankEingabe bankSenden={bankSenden} vorschlag={anzeige.zusammenfassung.find((z) => z.label === 'Mitglied')?.wert} />
              : <p className="uv-hinweis">Bitte die Seite neu laden.</p>
          ) : (<>
          {anzeige.haken.length > 0 && <p className="uv-abschnitt">Bestätigungen</p>}
          {anzeige.haken.map((h) => (
            <label key={h.key} className={`uv-haken ${haken[h.key] ? 'uv-an' : ''}`} data-testid={`uv-haken-${h.key}`}>
              <input type="checkbox" checked={!!haken[h.key]} onChange={(e) => setze(h.key, e.target.checked)} />
              <span className="uv-box" aria-hidden="true"><Check strokeWidth={3.2} /></span>
              <span className="uv-htext">{h.text}</span>
            </label>
          ))}
          <p className="uv-abschnitt">Unterschrift</p>
          {anzeige.unterschriften.map((u) => (
            <div key={u.key} className="uv-feld">
              <Unterschrift label={u.label} wert={unterschriften[u.key] || null} hoehe={einspaltig ? 150 : 170}
                onChange={(v) => setUnterschriften((x) => ({ ...x, [u.key]: v }))} tid={`uv-sig-${u.key}`} />
              {u.name && <p className="uv-name">{u.name}</p>}
            </div>
          ))}
          <button type="button" className="uv-knopf" disabled={!!fehlt || sendet} onClick={senden} data-testid="uv-unterschreiben">
            {sendet ? <><Loader2 className="w-5 h-5 animate-spin" /> Wird übertragen …</> : <><ShieldCheck className="w-5 h-5" /> Unterschreiben</>}
          </button>
          {fehlt && !sendet && <p className="uv-fehlt" data-testid="uv-fehlt">Es fehlt noch: {fehlt}.</p>}
          {fehler && <div className="uv-fehler" role="alert" data-testid="uv-fehler">{fehler}</div>}
          </>)}
          <p className="uv-hinweis">{anzeige.hinweis}</p>
        </div>
      </div>
    </div>
  );
}
